<script setup lang="ts">
    /**
     * Content som ska renderas i footern i ReceptforskrivningForm.vue
     */

    import KButton from "@/components/Button.vue";
    import { useStore } from "@/store";
    import { StoredReceptforskrivning } from "@/store/modules/recept/receptforskrivningModule";
    import ApotekAutocomplete from "@/tabs/Receptforskrivning/ApotekAutocomplete.vue";
    import TabFooter from "@/tabs/TabFooter.vue";
    import { PersonIdTyp } from "@/utils/constants";
    import { hasPersonnummer } from "@/utils/patient/patientData";
    import { Portal } from "portal-vue";
    import emitter from "tiny-emitter/instance";
    import { computed, nextTick, onMounted, toRefs } from "vue";

    const store = useStore();

    const patient = computed(() => store.getters["patientData/patient"]);

    const props = defineProps<{
        /**
         * Används för att få formulärkomponenterna att registrera sig med formuläret efter rendering
         * och för att targetta rätt tab med portalen. Behövs för att
         * formulärkomponenterna här renderas utanför KForm. Se ReceptforskrivningForm.vue hur det görs och används.
         * Du behöver troligen inte overrida det som sätts där.
         */
        tabId: number;
        /**
         * Används för att få formulärkomponenterna att registrera sig med formuläret efter rendering. Behövs för att
         * formulärkomponenterna här renderas utanför KForm. Se ReceptforskrivningForm.vue hur det görs och används.
         * Du behöver troligen inte overrida det som sätts där.
         */
        formIdPrefix: string;
        receptforskrivningId?: string;
        /**
         * Är det en förskrivning i en samling som redigeras?
         */
        editingSamling?: boolean;
        formRef?: any;
    }>();

    const { receptforskrivningId, editingSamling, formRef } = toRefs(props);

    const emit = defineEmits<{
        (e: "accept"): void;
        (e: "cancel"): void;
        (e: "action", payload: { step: "form" | "display" | "list"; id?: string }): void;
    }>();

    const onAccept = () => {
        emit("accept");
    };

    const onCancel = () => {
        if (store.getters["receptforskrivning/receptforskrivningar"].length) {
            if (editingSamling?.value) {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Vill du avbryta redigeringen?",
                    buttons: [
                        {
                            title: "Fortsätt redigera förskrivningen",
                            type: "secondary",
                        },
                        {
                            title: "Ja, stäng utan ändringar",
                            type: "primary",
                            action: () => {
                                emit("action", { step: "list" });
                            },
                        },
                    ],
                });
            } else {
                emitter.emit("open-dialog", {
                    type: "warning",
                    description: "Förskrivningen har inte skickats. Vill du stänga utan att skicka den?",
                    buttons: [
                        {
                            title: "Fortsätt redigera förskrivningen",
                            type: "secondary",
                        },
                        {
                            title: "Ja, stäng utan att skicka den",
                            type: "primary",
                            action: () => {
                                store.commit("receptforskrivning/removeReceptforskrivning", {
                                    id: receptforskrivningId?.value,
                                });

                                emit("action", { step: "list" });
                            },
                        },
                    ],
                });
            }

            return;
        }

        emit("cancel");
    };

    onMounted(async () => {
        await nextTick();

        const receptforskrivningar: StoredReceptforskrivning[] =
            store.getters["receptforskrivning/receptforskrivningar"];

        if (receptforskrivningar?.length) {
            (
                formRef?.value as {
                    setFieldValue: (props: { name: string; value: StoredReceptforskrivning["apotek"] | null }) => void;
                }
            )?.setFieldValue?.({ name: "apotek", value: receptforskrivningar?.[0]?.apotek ?? null });
        }
    });
</script>

<template>
    <portal :to="`tab-${tabId}`">
        <TabFooter>
            <div class="receptforskrivning-form-portal">
                <div class="form-content">
                    <KGroup
                        name="forhandsvisning"
                        class="span-all free-flow forhandsvisning-group"
                        direction="horizontal"
                    >
                        <div class="section">
                            <ApotekAutocomplete
                                v-if="!hasPersonnummer({ personIdType: patient?.personIdType })"
                                label="Skicka till apotek"
                                validation="required"
                                :registerOnMountId="`${formIdPrefix}${tabId}`"
                                :formRef="formRef"
                                class="apotek"
                            />
                            <KText
                                name="forhandsvisning"
                                label="Förhandsvisning doseringsanvisning"
                                type="textarea"
                                :registerOnMountId="`${formIdPrefix}${tabId}`"
                                readonly
                                disabled
                                class="forhandsvisning"
                                :validation="[
                                    ['required'],
                                    ['max', patient?.personIdType === PersonIdTyp.PERSONNUMMER ? 742 : 400],
                                ]"
                                :maxCount="patient?.personIdType === PersonIdTyp.PERSONNUMMER ? 742 : 400"
                            />
                        </div>
                        <KText
                            name="administreringsinstruktion"
                            validation="required"
                            :registerOnMountId="`${formIdPrefix}${tabId}`"
                            :hidden="true"
                        />
                        <div class="actions">
                            <KButton type="button" size="slim" variant="standard" @click="onAccept">
                                Förhandsgranska
                            </KButton>
                            <KButton type="button" size="small" variant="contour" @click="onCancel">Avbryt</KButton>
                        </div>
                    </KGroup>
                </div>
            </div>
        </TabFooter>
    </portal>
</template>

<style scoped lang="scss">
    @use "@/components/forms/Form/styles/FormContent";
    @import "@/style/form";

    .receptforskrivning-form-portal {
        @include FormContent.content;

        .forhandsvisning-group {
            ::v-deep .group-content {
                align-items: flex-end;

                .section {
                    display: flex;
                    flex-direction: column;
                    flex-grow: 1;
                    gap: FormContent.$form-column-gap-thin;

                    .apotek {
                        max-width: 30rem;
                    }

                    .forhandsvisning {
                        min-width: FormContent.$k-form-column-width;
                        max-width: 100%;
                        flex-grow: 2;
                    }
                }
                .actions {
                    display: flex;
                    align-items: flex-end;
                    align-self: flex-start;
                    width: fit-content;
                    max-width: 100%;
                    column-gap: calc($form-column-gap / 2);
                    margin-top: auto;
                    // Storleken på suffix
                    padding-bottom: 1.1rem;
                }
            }
        }
    }
</style>
